import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import {
  RdsBadgeModule,
  RdsButtonModule,
  RdsDropdownModule,
  RdsFormFieldModule,
  RdsIconsModule,
  RdsListItemModule,
  RdsPaginatorComponent,
  RdsPaginatorModule,
  RdsSearchModule,
  RdsTabModule,
  RdsTooltipModule,
} from '@rds/angular-components';
import { OfferingsService } from '../../../services/offerings.service';
import { FocusCardComponent } from '../focus-card/focus-card.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Role } from '../../../enums/role.enum';
import { AuthService } from '../../../services/auth.service';
import { BUTTON_TEXTS } from '../../../constants/buttons.constants';
import { OFFERING_TYPE_ROUTE } from '../../../constants/offering-routes.constants';
import { OfferingType } from '../../../enums/offering-type.enum';
import { FeatureFlagDirective } from '../../../directives/feature-flag.directive';
import {MY_OFFERINGS_TEXTS} from '../../../constants/my-offerings-contants';
import { CardSkeletonComponent } from '../../shared/card-skeleton/card-skeleton.component';

@Component({
  selector: 'inexp-focus-my-offerings',
  imports: [
    RdsButtonModule,
    RdsIconsModule,
    RouterModule,
    RdsTabModule,
    RdsBadgeModule,
    FocusCardComponent,
    RdsSearchModule,
    FormsModule,
    ReactiveFormsModule,
    RdsFormFieldModule,
    RdsPaginatorModule,
    RdsTooltipModule,
    RdsDropdownModule,
    RdsListItemModule,
    FeatureFlagDirective,
    CardSkeletonComponent,
  ],
  templateUrl: './focus-my-offerings.component.html',
  styleUrl: './focus-my-offerings.component.scss',
})
export class FocusMyOfferingsComponent {
  private readonly _offeringsService = inject(OfferingsService);
  private readonly _authService = inject(AuthService);
  isLoading = computed(() => this._offeringsService.isLoadingDraftOfferings());
  /** Placeholder slots rendered while offerings load. */
  readonly skeletonItems = Array.from({ length: 6 });
  readonly BUTTONS = BUTTON_TEXTS;
  readonly OFFERING_TYPE_ROUTE = OFFERING_TYPE_ROUTE;
  readonly OfferingType = OfferingType;
  readonly OFFERINGS_TEXTS = MY_OFFERINGS_TEXTS;
  offerings = this._offeringsService.draftOfferings;
  totalOfferings = computed(() => this.filteredAndSortedOfferings().length);
  itemsPerPage = signal(10);
  currentPage = signal(0);
  searchQuery = signal('');
  sortOrder = signal<'newest' | 'oldest'>('newest');
  selectedRole = signal<Role | 'all'>('all');
  roles = Object.values(Role);
  readonly userEmail = this._authService.email;
  readonly isModerator = this._authService.isModerator;
  readonly paginator = viewChild(RdsPaginatorComponent);

  paginatedOfferings = computed(() => {
    const start = this.currentPage() * this.itemsPerPage();
    return this.filteredAndSortedOfferings().slice(
      start,
      start + this.itemsPerPage(),
    );
  });

  filteredAndSortedOfferings = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const sort = this.sortOrder();
    const userEmail = this.userEmail()?.toLowerCase() ?? '';
    const selectedRole = this.selectedRole();
    const isModerator = this.isModerator();

    // filtering by query & user relationship.
    // Moderators see every offering, not just the ones they are linked to.
    let base = this.offerings()
      .filter((o) => o.displayName?.toLowerCase().includes(query))
      .filter((o) => {
        if (isModerator) return true;
        const owner = o.ownerEmail?.toLowerCase().trim();
        const deputy = o.deputyEmail?.toLowerCase().trim();
        const authors = (o.authorEmails ?? []).map((e: string) =>
          e?.toLowerCase?.().trim(),
        );
        return (
          owner === userEmail ||
          deputy === userEmail ||
          authors.includes(userEmail)
        );
      });

    // adding ALL roles for the user
    const withRoles = base.map((o) => {
      const owner = o.ownerEmail?.toLowerCase().trim();
      const deputy = o.deputyEmail?.toLowerCase().trim();
      const authors = (o.authorEmails ?? []).map((e: string) =>
        e?.toLowerCase?.().trim(),
      );

      const myRoles: Role[] = [];
      if (owner === userEmail) myRoles.push(Role.OWNER);
      if (deputy === userEmail) myRoles.push(Role.DEPUTY);
      if (authors.includes(userEmail)) myRoles.push(Role.AUTHOR);
      // Tag offerings a moderator only sees via their elevated access.
      if (isModerator && myRoles.length === 0) myRoles.push(Role.MODERATOR);

      return { ...o, myRoles };
    });

    // filtering by role - check if selectedRole is in the user's roles
    const roleFiltered = withRoles.filter((o) =>
      selectedRole === 'all' ? true : o.myRoles.includes(selectedRole),
    );

    return roleFiltered.sort((a, b) => {
      const dateA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const dateB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
      return sort === 'newest' ? dateB - dateA : dateA - dateB;
    });
  });

  handlePageEvent(event: any) {
    this.currentPage.set(event.pageIndex);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
